import React, { useEffect, useState } from 'react';
import { DollarSignIcon, ChevronRightIcon, BookIcon, PlusIcon, RefreshCwIcon, SearchIcon, FilterIcon, SettingsIcon, UploadIcon, AlertTriangleIcon } from 'lucide-react';
import { ExchangeRateTable } from './exchange/ExchangeRateTable';
import { ExchangeRateForm } from './exchange/ExchangeRateForm';
import { ExternalSubscriptionForm } from './exchange/ExternalSubscriptionForm';
import { RateHistoryView } from './exchange/RateHistoryView';
import { ExchangeRateUpload } from './exchange/ExchangeRateUpload';
// Define types
export type ExchangeRateType = 'Spot' | 'Contract' | 'Corporate' | 'Bank' | 'Custom';
export type OrgUnitScope = 'Global' | 'OrgUnit';
export interface ExchangeRate {
  id: string;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  inverseRate: number;
  type: ExchangeRateType;
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  scope: OrgUnitScope;
  isActive: boolean;
  lastUpdated: string;
  updatedBy?: string;
}
export interface RateHistory {
  id: string;
  rateId: string;
  fromCurrency: string;
  toCurrency: string;
  rate: number;
  date: string;
  type: ExchangeRateType;
  source: string;
}
export function CurrencyExchangeView({
  locale = 'en-US'
}) {
  // States
  const [exchangeRates, setExchangeRates] = useState<ExchangeRate[]>([]);
  const [selectedRate, setSelectedRate] = useState<ExchangeRate | null>(null);
  const [showRateForm, setShowRateForm] = useState(false);
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [showRateHistory, setShowRateHistory] = useState(false);
  const [showRateUpload, setShowRateUpload] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    fromCurrency: '',
    toCurrency: '',
    type: '',
    status: '',
    searchTerm: ''
  });
  // Load sample data
  useEffect(() => {
    const sampleData: ExchangeRate[] = [{
      id: 'rate-001',
      fromCurrency: 'USD',
      toCurrency: 'EUR',
      rate: 0.92,
      inverseRate: 1.09,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-002',
      fromCurrency: 'USD',
      toCurrency: 'GBP',
      rate: 0.78,
      inverseRate: 1.28,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-003',
      fromCurrency: 'USD',
      toCurrency: 'CAD',
      rate: 1.37,
      inverseRate: 0.73,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-004',
      fromCurrency: 'USD',
      toCurrency: 'AUD',
      rate: 1.55,
      inverseRate: 0.65,
      type: 'Custom',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-18T14:25:00Z',
      updatedBy: 'John Smith'
    }, {
      id: 'rate-005',
      fromCurrency: 'EUR',
      toCurrency: 'GBP',
      rate: 0.87,
      inverseRate: 1.15,
      type: 'Spot',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'System'
    }, {
      id: 'rate-006',
      fromCurrency: 'USD',
      toCurrency: 'JPY',
      rate: 149.75,
      inverseRate: 0.0067,
      type: 'Bank',
      scope: 'Global',
      isActive: true,
      lastUpdated: '2023-10-20T08:30:00Z',
      updatedBy: 'OpenExchangeRates API'
    }, {
      id: 'rate-007',
      fromCurrency: 'USD',
      toCurrency: 'MXN',
      rate: 18.25,
      inverseRate: 0.055,
      type: 'Custom',
      orgUnitId: 'ou-latam-mx',
      orgUnitName: 'Mexico Office',
      orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
      scope: 'OrgUnit',
      isActive: true,
      lastUpdated: '2023-10-15T10:15:00Z',
      updatedBy: 'Maria Rodriguez'
    }, {
      id: 'rate-008',
      fromCurrency: 'EUR',
      toCurrency: 'USD',
      rate: 1.09,
      inverseRate: 0.92,
      type: 'Contract',
      orgUnitId: 'ou-emea-uk',
      orgUnitName: 'UK Office',
      orgUnitPath: 'Global Technologies Inc. > EMEA Region > UK Office',
      scope: 'OrgUnit',
      isActive: true,
      lastUpdated: '2023-10-10T16:45:00Z',
      updatedBy: 'Contract #C-2023-056'
    }, {
      id: 'rate-009',
      fromCurrency: 'USD',
      toCurrency: 'SGD',
      rate: 1.35,
      inverseRate: 0.74,
      type: 'Corporate',
      orgUnitId: 'ou-apac-sg',
      orgUnitName: 'Singapore Office',
      orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
      scope: 'OrgUnit',
      isActive: true,
      lastUpdated: '2023-10-12T09:30:00Z',
      updatedBy: 'Finance Team'
    }];
    setExchangeRates(sampleData);
  }, []);
  // Apply filters and separate by scope
  const filteredRates = exchangeRates.filter(rate => {
    if (filters.fromCurrency && rate.fromCurrency !== filters.fromCurrency) return false;
    if (filters.toCurrency && rate.toCurrency !== filters.toCurrency) return false;
    if (filters.type && rate.type !== filters.type) return false;
    if (filters.status) {
      const isActive = filters.status === 'Active';
      if (rate.isActive !== isActive) return false;
    }
    if (filters.searchTerm) {
      const term = filters.searchTerm.toLowerCase();
      const matchesSearch = rate.fromCurrency.toLowerCase().includes(term) || rate.toCurrency.toLowerCase().includes(term) || rate.orgUnitName && rate.orgUnitName.toLowerCase().includes(term) || rate.orgUnitPath && rate.orgUnitPath.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    return true;
  });
  const globalRates = filteredRates.filter(r => r.scope === 'Global');
  const orgUnitRates = filteredRates.filter(r => r.scope === 'OrgUnit');
  // Handle adding a new rate
  const handleAddRate = () => {
    setSelectedRate(null);
    setShowRateForm(true);
    setShowSubscriptionForm(false);
    setShowRateHistory(false);
    setShowRateUpload(false);
  };
  // Handle editing a rate
  const handleEditRate = (rate: ExchangeRate) => {
    setSelectedRate(rate);
    setShowRateForm(true);
    setShowSubscriptionForm(false);
    setShowRateHistory(false);
    setShowRateUpload(false);
  };
  // Handle viewing rate history
  const handleViewHistory = (rate: ExchangeRate) => {
    setSelectedRate(rate);
    setShowRateHistory(true);
    setShowRateForm(false);
    setShowSubscriptionForm(false);
    setShowRateUpload(false);
  };
  // Handle toggling rate active status
  const handleToggleStatus = (rateId: string) => {
    setExchangeRates(prevRates => prevRates.map(rate => rate.id === rateId ? {
      ...rate,
      isActive: !rate.isActive,
      lastUpdated: new Date().toISOString(),
      updatedBy: 'Current User'
    } : rate));
  };
  // Handle deleting a rate
  const handleDeleteRate = (rateId: string) => {
    setDeletingId(rateId);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      setExchangeRates(exchangeRates.filter(r => r.id !== deletingId));
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  // Handle saving a rate
  const handleSaveRate = (rate: ExchangeRate) => {
    if (rate.id) {
      // Update existing rate
      setExchangeRates(prevRates => prevRates.map(r => r.id === rate.id ? rate : r));
    } else {
      // Add new rate
      const newRate = {
        ...rate,
        id: `rate-${Date.now()}`,
        lastUpdated: new Date().toISOString(),
        updatedBy: 'Current User'
      };
      setExchangeRates(prevRates => [...prevRates, newRate]);
    }
    setShowRateForm(false);
  };
  // Handle filter changes
  const handleFilterChange = (name: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };
  // Clear all filters
  const clearFilters = () => {
    setFilters({
      fromCurrency: '',
      toCurrency: '',
      type: '',
      status: '',
      searchTerm: ''
    });
  };
  // Show external subscription settings
  const handleShowSubscriptionSettings = () => {
    setShowSubscriptionForm(true);
    setShowRateForm(false);
    setShowRateHistory(false);
    setShowRateUpload(false);
  };
  // Handle running a sync now
  const handleRunSync = () => {
    alert('Synchronization with external provider initiated.');
  };
  // Handle importing rates from upload
  const handleImportRates = (rates: ExchangeRate[]) => {
    const updatedRates = [...exchangeRates];
    rates.forEach(newRate => {
      const existingIndex = updatedRates.findIndex(rate => rate.fromCurrency === newRate.fromCurrency && rate.toCurrency === newRate.toCurrency && rate.scope === newRate.scope && rate.orgUnitId === newRate.orgUnitId);
      if (existingIndex >= 0) {
        updatedRates[existingIndex] = {
          ...updatedRates[existingIndex],
          rate: newRate.rate,
          inverseRate: newRate.inverseRate,
          type: newRate.type,
          isActive: newRate.isActive,
          lastUpdated: new Date().toISOString(),
          updatedBy: 'Import'
        };
      } else {
        updatedRates.push({
          ...newRate,
          id: `rate-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`
        });
      }
    });
    setExchangeRates(updatedRates);
    setShowRateUpload(false);
    alert(`Successfully imported ${rates.length} exchange rates.`);
  };
  return <div className="space-y-6" data-id="element-9524">
      <div className="mb-4" data-id="element-9525">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-9526">Currency Exchange</h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-9527">
          Manage exchange rates at tenant and organisation unit levels for
          multi-currency transactions
        </p>
      </div>

      {/* Filters and Actions */}
      <div className="bg-white shadow rounded-lg" data-id="element-9528">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6" data-id="element-9529">
          <div className="flex justify-between items-center" data-id="element-9530">
            <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center" data-id="element-9531">
              <DollarSignIcon className="h-5 w-5 mr-2 text-indigo-500" data-id="element-9532" />
              Exchange Rates
            </h3>
            <div className="flex items-center space-x-2" data-id="element-9533">
              <div className="relative rounded-md shadow-sm" data-id="element-9534">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9535">
                  <SearchIcon className="h-4 w-4 text-gray-400" data-id="element-9536" />
                </div>
                <input type="text" className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md" placeholder="Search rates..." value={filters.searchTerm} onChange={e => handleFilterChange('searchTerm', e.target.value)} data-id="element-9537" />
              </div>
              <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-9538">
                <FilterIcon className="h-4 w-4 mr-1" data-id="element-9539" />
                Filters
              </button>
              <button onClick={clearFilters} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-9540">
                <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-9541" />
                Reset
              </button>
            </div>
          </div>
        </div>

        {/* Filters panel */}
        {isFilterOpen && <div className="px-4 py-5 sm:px-6 border-b border-gray-200 bg-gray-50" data-id="element-9542">
            <div className="grid grid-cols-1 gap-y-4 gap-x-4 sm:grid-cols-4" data-id="element-9543">
              <div data-id="element-9544">
                <label htmlFor="fromCurrency" className="block text-sm font-medium text-gray-700" data-id="element-9545">
                  From Currency
                </label>
                <select id="fromCurrency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.fromCurrency} onChange={e => handleFilterChange('fromCurrency', e.target.value)} data-id="element-9546">
                  <option value="" data-id="element-9547">All</option>
                  <option value="USD" data-id="element-9548">USD</option>
                  <option value="EUR" data-id="element-9549">EUR</option>
                  <option value="GBP" data-id="element-9550">GBP</option>
                  <option value="CAD" data-id="element-9551">CAD</option>
                  <option value="AUD" data-id="element-9552">AUD</option>
                  <option value="JPY" data-id="element-9553">JPY</option>
                  <option value="SGD" data-id="element-9554">SGD</option>
                  <option value="MXN" data-id="element-9555">MXN</option>
                </select>
              </div>
              <div data-id="element-9556">
                <label htmlFor="toCurrency" className="block text-sm font-medium text-gray-700" data-id="element-9557">
                  To Currency
                </label>
                <select id="toCurrency" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.toCurrency} onChange={e => handleFilterChange('toCurrency', e.target.value)} data-id="element-9558">
                  <option value="" data-id="element-9559">All</option>
                  <option value="USD" data-id="element-9560">USD</option>
                  <option value="EUR" data-id="element-9561">EUR</option>
                  <option value="GBP" data-id="element-9562">GBP</option>
                  <option value="CAD" data-id="element-9563">CAD</option>
                  <option value="AUD" data-id="element-9564">AUD</option>
                  <option value="JPY" data-id="element-9565">JPY</option>
                  <option value="SGD" data-id="element-9566">SGD</option>
                  <option value="MXN" data-id="element-9567">MXN</option>
                </select>
              </div>
              <div data-id="element-9568">
                <label htmlFor="type" className="block text-sm font-medium text-gray-700" data-id="element-9569">
                  Rate Type
                </label>
                <select id="type" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.type} onChange={e => handleFilterChange('type', e.target.value)} data-id="element-9570">
                  <option value="" data-id="element-9571">All</option>
                  <option value="Spot" data-id="element-9572">Spot</option>
                  <option value="Contract" data-id="element-9573">Contract</option>
                  <option value="Corporate" data-id="element-9574">Corporate</option>
                  <option value="Bank" data-id="element-9575">Bank</option>
                  <option value="Custom" data-id="element-9576">Custom</option>
                </select>
              </div>
              <div data-id="element-9577">
                <label htmlFor="status" className="block text-sm font-medium text-gray-700" data-id="element-9578">
                  Status
                </label>
                <select id="status" className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md" value={filters.status} onChange={e => handleFilterChange('status', e.target.value)} data-id="element-9579">
                  <option value="" data-id="element-9580">All</option>
                  <option value="Active" data-id="element-9581">Active</option>
                  <option value="Inactive" data-id="element-9582">Inactive</option>
                </select>
              </div>
            </div>
          </div>}

        {/* Action buttons */}
        <div className="px-4 py-3 bg-gray-50 sm:px-6 flex justify-between" data-id="element-9583">
          <div className="flex space-x-2" data-id="element-9584">
            <button onClick={handleAddRate} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-9585">
              <PlusIcon className="h-4 w-4 mr-1" data-id="element-9586" />
              Add Exchange Rate
            </button>
            <button onClick={() => setShowRateUpload(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700" data-id="element-9587">
              <UploadIcon className="h-4 w-4 mr-1" data-id="element-9588" />
              Upload Rates
            </button>
            <button onClick={handleRunSync} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-9589">
              <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-9590" />
              Sync Now
            </button>
          </div>
          <button onClick={handleShowSubscriptionSettings} className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" data-id="element-9591">
            <SettingsIcon className="h-4 w-4 mr-1" data-id="element-9592" />
            External Provider Settings
          </button>
        </div>
      </div>

      {/* Two-section layout */}
      <div className="space-y-6" data-id="element-9593">
        {/* Global Exchange Rates */}
        <div className="bg-white shadow rounded-lg" data-id="element-9594">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9595">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9596">
              Global Exchange Rates
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9597">
              Global exchange rates available to all organisation units
            </p>
          </div>
          {globalRates.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-9598">
              <p className="text-sm text-gray-500" data-id="element-9599">
                No global exchange rates found.
              </p>
            </div> : <ExchangeRateTable rates={globalRates} onEdit={handleEditRate} onToggleStatus={handleToggleStatus} onViewHistory={handleViewHistory} onDelete={handleDeleteRate} locale={locale} data-id="element-9600" />}
        </div>

        {/* OrgUnit Exchange Rates */}
        <div className="bg-white shadow rounded-lg" data-id="element-9601">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-9602">
            <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9603">
              Organisation Unit Exchange Rates
            </h3>
            <p className="mt-1 text-sm text-gray-500" data-id="element-9604">
              Exchange rates specific to individual organisation units
            </p>
          </div>
          {orgUnitRates.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-9605">
              <p className="text-sm text-gray-500" data-id="element-9606">
                No organisation unit exchange rates found.
              </p>
            </div> : <ExchangeRateTable rates={orgUnitRates} onEdit={handleEditRate} onToggleStatus={handleToggleStatus} onViewHistory={handleViewHistory} onDelete={handleDeleteRate} locale={locale} showOrgUnit={true} data-id="element-9607" />}
        </div>
      </div>

      {/* Side panels */}
      {(showRateForm || showSubscriptionForm || showRateHistory || showRateUpload) && <div className="fixed inset-0 overflow-hidden z-10" data-id="element-9608">
          <div className="absolute inset-0 overflow-hidden" data-id="element-9609">
            <div className="absolute inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => {
          setShowRateForm(false);
          setShowSubscriptionForm(false);
          setShowRateHistory(false);
          setShowRateUpload(false);
        }} data-id="element-9610"></div>
            <div className="fixed inset-y-0 right-0 pl-10 max-w-full flex" data-id="element-9611">
              <div className="w-screen max-w-md" data-id="element-9612">
                <div className="h-full flex flex-col bg-white shadow-xl overflow-y-scroll" data-id="element-9613">
                  {showRateForm && <ExchangeRateForm rate={selectedRate} onSave={handleSaveRate} onCancel={() => setShowRateForm(false)} locale={locale} data-id="element-9614" />}
                  {showSubscriptionForm && <ExternalSubscriptionForm onClose={() => setShowSubscriptionForm(false)} onRunSync={handleRunSync} locale={locale} data-id="element-9615" />}
                  {showRateHistory && selectedRate && <RateHistoryView rate={selectedRate} onClose={() => setShowRateHistory(false)} locale={locale} data-id="element-9616" />}
                  {showRateUpload && <ExchangeRateUpload onImport={handleImportRates} onCancel={() => setShowRateUpload(false)} locale={locale} data-id="element-9617" />}
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-9618">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-9619">
            <div className="fixed inset-0 transition-opacity" data-id="element-9620">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-9621"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" data-id="element-9622">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-9623">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-9624">
                <div className="sm:flex sm:items-start" data-id="element-9625">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-9626">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-9627" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-9628">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-9629">
                      Delete Exchange Rate
                    </h3>
                    <div className="mt-2" data-id="element-9630">
                      <p className="text-sm text-gray-500" data-id="element-9631">
                        Are you sure you want to delete this exchange rate? This
                        action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-9632">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-9633">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-9634">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}